import { HttpTypes } from '@/types';
import { ICategoryTree } from '@/types/category';
import { IProduct } from '@/types/general';
import { BaseCustomPage } from '@/types/http/custom-pages/common';
import { AdminCurrentThemeResponse, AdminThemeLibraryListResponse } from '@/types/http/themes';
import { ILocationData } from '@/types/location';
import { IBuilder, ICollectionTemplate, INavigationMenuDetail } from '@/types/site';
import { AxiosInstance } from 'axios';

class Builder {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async listPage(queryParams: {
    page_name: string;
    theme_id?: string;
  }): Promise<HttpTypes.AdminBuilderResponse> {
    const result = await this.client.get('/sites/builder/page', {
      params: queryParams,
    });
    return result.data;
  }

  async retrievePage(pageId: string): Promise<HttpTypes.IResponse<IBuilder>> {
    const result = await this.client.get(`/sites/builder/page/${pageId}`);
    return result.data;
  }

  async retrieveBuilderCollectionTemplate(
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.IResponse<ICollectionTemplate[]>> {
    const result = await this.client.get('/sites/builder/collections/templates', {
      params: query,
    });
    return result.data;
  }

  async updatePage(data: unknown): Promise<HttpTypes.IResponse<IBuilder>> {
    const result = await this.client.post('/sites/builder/modify', data);
    return result.data;
  }

  async listCollectionTemplate(): Promise<HttpTypes.IResponse<ICollectionTemplate[]>> {
    const result = await this.client.get('/sites/collections/template');
    return result.data;
  }

  async createCollectionTemplate(data: unknown): Promise<HttpTypes.IResponse<unknown>> {
    const result = await this.client.post('/sites/collections/template', data);
    return result.data;
  }

  async retrieveMenu(id: string): Promise<HttpTypes.IResponse<INavigationMenuDetail>> {
    const result = await this.client.get(`/sites/navigation/${id}`);
    return result.data;
  }

  async listMenu(): Promise<HttpTypes.IResponse<INavigationMenuDetail[]>> {
    const result = await this.client.get('/sites/navigation');
    return result.data;
  }

  async listThemeUsers(queryParams: HttpTypes.FindParams): Promise<AdminThemeLibraryListResponse> {
    const result = await this.client.get('/sites/builder/theme/library', {
      params: queryParams,
    });
    return result.data;
  }

  async listPages(): Promise<HttpTypes.IResponse<BaseCustomPage[]>> {
    const result = await this.client.get('/sites/collections/template');
    return result.data;
  }

  async changeTheme(
    data: HttpTypes.AdminChangeThemePayload
  ): Promise<HttpTypes.IResponse<HttpTypes.AdminThemeLibrary>> {
    const result = await this.client.post('/sites/builder/theme/change', data);
    return result.data;
  }

  async retrieveCurrentTheme(): Promise<AdminCurrentThemeResponse> {
    const result = await this.client.get('/sites/builder/theme/current');
    return result.data;
  }

  async listThemeStoreLibraries(
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.AdminThemeLibraryListResponse> {
    const result = await this.client.get('/sites/themes', {
      params: query,
    });
    return result.data;
  }

  async listStore(query: { q: string; status: string }): Promise<HttpTypes.AdminStoreListResponse> {
    const result = await this.client.get('/sites/list', {
      params: query,
    });
    return result.data;
  }

  async listBrand(query: HttpTypes.FindParams): Promise<HttpTypes.AdminBrandListResponse> {
    const result = await this.client.get('/sites/brand', {
      params: query,
    });
    return result.data;
  }

  async listCategoryOmni(
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.IResponse<ICategoryTree[]>> {
    const result = await this.client.get('/sites/category-omni', {
      params: query,
    });
    return result.data;
  }

  async listCatalogOmni(
    query: HttpTypes.FindParams & {
      categoryIds?: string[];
    }
  ): Promise<HttpTypes.IResponse<IProduct[]>> {
    const result = await this.client.get('/sites/catalog-omni', { params: query });
    return result.data;
  }

  async listActiveLocationOmni(
    query: HttpTypes.FindParams
  ): Promise<HttpTypes.IResponse<ILocationData[]>> {
    const result = await this.client.get('/sites/location/active-omni', { params: query });
    return result.data;
  }

  async exportTheme(themeId: string): Promise<HttpTypes.AdminThemeLibrary> {
    const result = await this.client.post(`/sites/builder/theme/export`, {
      theme_id: themeId,
    });
    return result.data;
  }

  async deleteTheme(themeId: string): Promise<HttpTypes.IResponse<HttpTypes.AdminThemeLibrary>> {
    const result = await this.client.delete(`/sites/builder/theme/${themeId}`);
    return result.data;
  }

  async importTheme(theme: FormData): Promise<HttpTypes.AdminThemeLibrary> {
    const result = await this.client.post(`/sites/builder/theme/import`, theme, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return result.data;
  }

  async addTheme(themeId: string) {
    const result = await this.client.post(`/sites/add-theme`, { theme_id: themeId });
    return result.data;
  }
}

export default Builder;
